import { FormikHelpers } from "formik";

export const formikWrapperBag = <
  T extends Record<string, any> | Record<string, any>[]
>(
  data: T,
  customInitialValues: Record<string, any> = {}
) => {
  const makeInitialValues = (data: T) => {
    if (Array.isArray(data)) {
      return data.map((item) => processItem(item, customInitialValues));
    }
    return processItem(data as Record<string, any>, customInitialValues);
  };

  const processItem = (
    item: Record<string, any>,
    customValues: Record<string, any>
  ) => ({
    ...item,
    ...customValues,
    ...Object.entries(item).reduce((acc: Record<string, any>, [key, value]) => {
      if (Array.isArray(value)) {
        acc[key] = value; // Preserve arrays
      } else if (typeof value === "object" && value !== null) {
        acc[key] = value; // Keep objects as they are
      } else if (typeof value === "string") {
        acc[key] = value ?? ""; // Default to empty string
      } else if (typeof value === "number") {
        acc[key] = value ?? 0; // Default to 0
      } else if (typeof value === "boolean") {
        acc[key] = value ?? false; // Default to false
      } else {
        acc[key] = value ?? null; // Default to null for unknown types
      }
      return acc;
    }, {}),
  });

  const initialValues = makeInitialValues(data);

  const fieldNames: Record<string, string> = Array.isArray(initialValues)
    ? initialValues.length > 0
      ? Object.keys(initialValues[0]).reduce((acc, key) => {
          acc[key] = key;
          return acc;
        }, {} as Record<string, string>)
      : {}
    : Object.keys(initialValues).reduce((acc, key) => {
        acc[key] = key;
        return acc;
      }, {} as Record<string, string>);

  const handleCustomBlur = async (
    e: React.FocusEvent<any>,
    handleSubmit: () => Promise<void>,
    dirty: boolean,
    isSubmitting: boolean,
    setSubmitting: (isSubmitting: boolean) => void
  ): Promise<void> => {
    if (!["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName)) return;
    if (!dirty || isSubmitting) return;
    setSubmitting(true);
    await handleSubmit();
  };

  const handleKeyDown = async (
    e: React.KeyboardEvent<any>,
    handleSubmit: () => Promise<void>,
    dirty: boolean,
    isSubmitting: boolean,
    setSubmitting: (isSubmitting: boolean) => void
  ): Promise<void> => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!dirty || isSubmitting) return;
      setSubmitting(true);
      await handleSubmit();
    }
  };

  return {
    initialValues,
    fieldNames,
    handleKeyDown,
    handleCustomBlur,
  };
};
